import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import Drawer from '@material-ui/core/Drawer';
import { Alert, CustomInput, FormGroup } from 'reactstrap';

import Widget from '../../../components/Widget';
import NativeTable from '../../../components/NativeTable/NativeTable';
import {
  loadMenus, createMenu, updateMenu, archiveMenu, restoreMenu, persistMenuSortOrders,
  setMerchantDefaultMenu,
} from '../../../actions/menus';
import { withTranslation } from '../../../i18n/LanguageContext';
import s from './Menus.module.scss';

class Menus extends React.Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    menus: PropTypes.array,
    defaultMenuId: PropTypes.number,
    useMenuSchedule: PropTypes.bool,
    isReceiving: PropTypes.bool,
    isUpdating: PropTypes.bool,
    error: PropTypes.string,
  };

  static defaultProps = {
    menus: [],
    defaultMenuId: null,
    useMenuSchedule: false,
    isReceiving: false,
    isUpdating: false,
    error: '',
  };

  state = {
    showInactive: false,
    drawerOpen: false,
    draft: { MenuId: 0, Name: '', SortOrder: 0, IsActive: true },
    formError: '',
    saving: false,
  };

  componentDidMount() {
    this.props.dispatch(loadMenus(true));
  }

  openCreate = () => {
    const nextSort = (this.props.menus || []).reduce((max, m) => Math.max(max, m.SortOrder || 0), -1) + 1;
    this.setState({
      drawerOpen: true,
      formError: '',
      draft: { MenuId: 0, Name: '', SortOrder: nextSort, IsActive: true },
    });
  };

  openEdit = (menu) => {
    this.setState({
      drawerOpen: true,
      formError: '',
      draft: {
        MenuId: menu.MenuId,
        Name: menu.Name || '',
        SortOrder: menu.SortOrder || 0,
        IsActive: !!menu.IsActive,
      },
    });
  };

  closeDrawer = () => {
    if (this.state.saving) return;
    this.setState({ drawerOpen: false, formError: '' });
  };

  changeDraft = (field, value) => {
    this.setState({ draft: { ...this.state.draft, [field]: value } });
  };

  saveDraft = async () => {
    const { draft } = this.state;
    const name = (draft.Name || '').trim();
    if (!name) {
      this.setState({ formError: this.props.t('productsTabs.menuNameRequired') });
      return;
    }
    this.setState({ saving: true, formError: '' });
    let ok = false;
    if (draft.MenuId > 0) {
      ok = await this.props.dispatch(updateMenu(draft.MenuId, {
        MenuId: draft.MenuId,
        Name: name,
        SortOrder: Number(draft.SortOrder) || 0,
        IsActive: !!draft.IsActive,
      }));
    } else {
      const id = await this.props.dispatch(createMenu({
        Name: name,
        SortOrder: Number(draft.SortOrder) || 0,
      }));
      ok = !!id;
    }
    this.setState({ saving: false });
    if (ok) this.setState({ drawerOpen: false });
  };

  moveSort = async (menu, direction) => {
    const { showInactive } = this.state;
    const sorted = (this.props.menus || [])
      .filter((m) => showInactive || m.IsActive)
      .sort((a, b) => (a.SortOrder - b.SortOrder) || String(a.Name || '').localeCompare(String(b.Name || '')));

    const idx = sorted.findIndex((m) => m.MenuId === menu.MenuId);
    const swapWith = idx + direction;
    if (idx < 0 || swapWith < 0 || swapWith >= sorted.length) return;

    const next = [...sorted];
    const tmp = next[idx];
    next[idx] = next[swapWith];
    next[swapWith] = tmp;

    await this.props.dispatch(persistMenuSortOrders(next));
  };

  renderActions = (menu) => {
    const isDefault = this.props.defaultMenuId === menu.MenuId;
    return (
    <div className="d-flex align-items-center" style={{ gap: 8 }}>
      <Link to={`/admin/products/menus/${menu.MenuId}`} className="btn btn-sm btn-primary">
        {this.props.t('common.open')}
      </Link>
      <button type="button" className="btn btn-sm btn-secondary" onClick={() => this.openEdit(menu)}>
        {this.props.t('common.edit')}
      </button>
      <button type="button" className="btn btn-sm btn-secondary" onClick={() => this.moveSort(menu, -1)} title={this.props.t('productsTabs.moveUp')}>
        ↑
      </button>
      <button type="button" className="btn btn-sm btn-secondary" onClick={() => this.moveSort(menu, 1)} title={this.props.t('productsTabs.moveDown')}>
        ↓
      </button>
      {menu.IsActive && (
        <button
          type="button"
          className={`btn btn-sm ${isDefault ? 'btn-success' : 'btn-secondary'}`}
          disabled={isDefault || this.props.isUpdating}
          onClick={() => this.props.dispatch(setMerchantDefaultMenu(menu.MenuId))}
          title={this.props.t('productsTabs.setDefaultTitle')}
        >
          {isDefault ? this.props.t('common.default') : this.props.t('productsTabs.setDefault')}
        </button>
      )}
      {menu.IsActive ? (
        <button type="button" className="btn btn-sm btn-danger" onClick={() => this.props.dispatch(archiveMenu(menu.MenuId))}>
          {this.props.t('productsTabs.archive')}
        </button>
      ) : (
        <button type="button" className="btn btn-sm btn-success" onClick={() => this.props.dispatch(restoreMenu(menu.MenuId))}>
          {this.props.t('productsTabs.restore')}
        </button>
      )}
    </div>
    );
  };

  render() {
    const { menus, defaultMenuId, useMenuSchedule, error, isReceiving, isUpdating } = this.props;
    const { showInactive, drawerOpen, draft, formError, saving } = this.state;
    const visible = (menus || [])
      .filter((m) => showInactive || m.IsActive)
      .slice()
      .sort((a, b) => (a.SortOrder - b.SortOrder) || String(a.Name).localeCompare(String(b.Name)));
    const { t } = this.props;
    const namedDefault = defaultMenuId
      ? (visible.find((m) => m.MenuId === defaultMenuId)?.Name
        || (menus || []).find((m) => m.MenuId === defaultMenuId)?.Name
        || `Menu #${defaultMenuId}`)
      : t('productsTabs.allFullCatalog');
    const defaultLabel = useMenuSchedule
      ? t('productsTabs.autoScheduleWithFallback', { default: namedDefault })
      : namedDefault;

    return (
      <div className={s.page}>
        {(error) && <Alert color="danger" className="alert-sm">{error}</Alert>}
        <Widget>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 12 }}>
            <div>
              <h5 className="mb-1">{t('productsTabs.menusTitle')}</h5>
              <small className="text-muted">
                {t('productsTabs.menusIntro', { default: defaultLabel })}
                {useMenuSchedule ? t('productsTabs.menusIntroSchedule') : ''}
              </small>
            </div>
            <div className="d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
              <FormGroup check className="mb-0">
                <CustomInput
                  type="checkbox"
                  id="showInactiveMenus"
                  label={t('productsTabs.showArchived')}
                  checked={showInactive}
                  onChange={(e) => this.setState({ showInactive: e.target.checked })}
                />
              </FormGroup>
              <button
                type="button"
                className={`btn ${useMenuSchedule ? 'btn-success' : 'btn-secondary'}`}
                disabled={isUpdating}
                onClick={() => this.props.dispatch(setMerchantDefaultMenu(
                  defaultMenuId,
                  { useSchedule: !useMenuSchedule },
                ))}
                title={useMenuSchedule
                  ? t('productsTabs.autoScheduleOnTitle')
                  : t('productsTabs.autoScheduleTitle')}
              >
                {useMenuSchedule ? t('productsTabs.autoScheduleOn') : t('productsTabs.useAutoSchedule')}
              </button>
              {defaultMenuId ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={isUpdating}
                  onClick={() => this.props.dispatch(setMerchantDefaultMenu(null))}
                >
                  {t('productsTabs.clearDefault')}
                </button>
              ) : null}
              <button type="button" className="btn btn-success" onClick={this.openCreate} disabled={isUpdating}>
                {t('productsTabs.addMenu')}
              </button>
            </div>
          </div>

          {visible.length === 0 && !isReceiving ? (
            <Alert color="info" className="alert-sm">
              {t('productsTabs.noMenus')}
            </Alert>
          ) : (
            <NativeTable
              data={visible}
              columns={[
                { key: 'Name', header: t('common.name') },
                { key: 'SortOrder', header: t('productsTabs.order') },
                {
                  key: 'IsActive',
                  header: t('common.status'),
                  render: (cell, row) => {
                    const bits = [cell ? t('common.active') : t('common.archived')];
                    if (defaultMenuId === row.MenuId) bits.push(t('common.default'));
                    return bits.join(' · ');
                  },
                },
                {
                  key: 'MenuId',
                  header: t('common.actions'),
                  render: (_cell, row) => this.renderActions(row),
                },
              ]}
            />
          )}
        </Widget>

        <Drawer anchor="right" open={drawerOpen} onClose={this.closeDrawer}>
          <div style={{ width: 360, padding: 20 }}>
            <h5>{draft.MenuId ? t('productsTabs.editMenu') : t('productsTabs.addMenu')}</h5>
            {formError && <Alert color="danger" className="alert-sm">{formError}</Alert>}
            <FormGroup>
              <label>{t('common.name')}</label>
              <input
                className="form-control"
                value={draft.Name}
                onChange={(e) => this.changeDraft('Name', e.target.value)}
                disabled={saving}
              />
            </FormGroup>
            <FormGroup>
              <label>{t('productsTabs.sortOrder')}</label>
              <input
                type="number"
                className="form-control"
                value={draft.SortOrder}
                onChange={(e) => this.changeDraft('SortOrder', e.target.value)}
                disabled={saving}
              />
            </FormGroup>
            {draft.MenuId > 0 && (
              <FormGroup check>
                <CustomInput
                  type="checkbox"
                  id="menuActive"
                  label={t('common.active')}
                  checked={!!draft.IsActive}
                  onChange={(e) => this.changeDraft('IsActive', e.target.checked)}
                  disabled={saving}
                />
              </FormGroup>
            )}
            <div className="d-flex justify-content-end mt-3" style={{ gap: 8 }}>
              <button type="button" className="btn btn-secondary" onClick={this.closeDrawer} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-primary" onClick={this.saveDraft} disabled={saving}>
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </Drawer>
      </div>
    );
  }
}

function mapStateToProps(store) {
  return {
    menus: store.menus.list,
    defaultMenuId: store.menus.defaultMenuId,
    useMenuSchedule: store.menus.useMenuSchedule,
    isReceiving: store.menus.isReceiving,
    isUpdating: store.menus.isUpdating,
    error: store.menus.error,
  };
}

export default withRouter(connect(mapStateToProps)(withTranslation(Menus)));
